import { z } from "zod";

/**
 * RFC 9457 ProblemDetails error body, as emitted by the server for every
 * /api error (the ApiExceptionFilter and the [ApiController] auto-400).
 * Success responses carry the bare payload, not this shape.
 *
 * Hand-authored rather than generated: the server produces this shape from the
 * runtime exception filter, so it never reaches ApiExplorer and is absent from
 * the OpenAPI lockfile; and a ProblemDetails body is open by design (RFC 9457
 * extension members such as the unpublish 409's `blockedBy`, plus ASP.NET's
 * `traceId`, ride alongside the standard members), whereas the wire generator
 * emits closed `strictObject` shapes. Hence a lenient `looseObject`: standard
 * members are typed, anything else passes through as an extension member.
 * `errors` is the ValidationProblemDetails per-field map. Consumed by
 * `HttpUtil.parseError`, which validates leniently (a non-conformant body, e.g.
 * a gateway 502, falls back to best-effort rather than being discarded).
 */
export const ProblemDetailsWireSchema = z.looseObject({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  traceId: z.string().optional(),
  errors: z.record(z.string(), z.array(z.string())).optional(),
});

export type IProblemDetails = z.infer<typeof ProblemDetailsWireSchema>;

/**
 * The standard RFC 9457 members (plus ASP.NET's traceId): the keys the schema
 * declares. `HttpUtil.parseError` treats everything NOT in this set as an
 * extension member. Derived from the schema so the two cannot drift.
 */
export const PROBLEM_STANDARD_KEYS = Object.keys(
  ProblemDetailsWireSchema.shape,
) as (keyof typeof ProblemDetailsWireSchema.shape)[];

export class ApiResponse {
  public static Error(
    status: number,
    message: string,
    errors?: Record<string, string[]> | null,
    data: any = null,
  ) {
    return new ApiResponse(status, message, data, errors ?? null);
  }

  // No message: the server success-message channel is retired (toasts use
  // client strings), so a success response has no text to carry.
  public static Success(status: number, data: any = null) {
    return new ApiResponse(status, "", data, null);
  }

  public static File(status: number, message: string, blob: Blob) {
    return new ApiResponse(status, message, null, null, blob);
  }

  private constructor(
    public readonly status: number,
    public readonly message: string,
    public readonly data: any | null = null,
    public readonly errors: Record<string, string[]> | null = null,
    public readonly blob: Blob | null = null,
  ) {}

  public getErrorList(): string[] | null {
    if (!this.errors) {
      return null;
    }

    return Object.values(this.errors).flat();
  }

  public get isSuccess(): boolean {
    return this.status >= 200 && this.status < 400;
  }

  public get isFile(): boolean {
    return this.blob != null;
  }

  /**
   * Plain accessor for the response payload. Validation does NOT happen here:
   * it happens once, at response-parse time in `HttpUtil.parseData`, driven by
   * the `serializer` (entities) or `schema` (off-entity sub-resources) the
   * request declared in its `IApiRequestArgs`. A read whose request carried
   * neither is unvalidated; give the request a schema rather than checking
   * here. See design/zod-frontend-validation/PLAN.md.
   */
  public getData<T>(): T {
    return this.data as T;
  }

  toString(): string {
    return this.message;
  }
}
